import React, { useEffect, useRef } from "react";
import { Button, Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { DELIVERY_DOCKET, DRAWING_NUMBER, JOB_CODE, JOB_LOCATION, STATUS, TEXT_APPLY, TEXT_CLEAR, TEXT_FILTERS } from "@/constants/strings.ts";
import { THEME } from "@/constants";
import { DropdownSelector } from "@/components/dropdown/DropDown.tsx";
import { useShallow } from "zustand/shallow";
import { useViewProjectItemDetailStore } from "@/stores/viewProjectItemDetailStore.ts";

interface ViewProjectItemDetailItemFiltersProps {
  isVisible: boolean;
  onCancel: () => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
}

export const ViewProjectItemDetailItemFiltersModal: React.FC<ViewProjectItemDetailItemFiltersProps> = ({
  isVisible,
  onCancel,
  onClearFilters,
  onApplyFilters
}) => {
  const {
    filters,
    selectedStatus,
    setSelectedStatus,
    selectedDeliveryDocket,
    setSelectedDeliveryDocket,
    selectedDrawingNumbers,
    setSelectedDrawingNumbers,
    selectedJobCodes,
    setSelectedJobCodes,
    selectedJobLocations,
    setSelectedJobLocations
  } = useViewProjectItemDetailStore(
    useShallow(state => ({
      filters: state.filters,
      selectedStatus: state.selectedStatus,
      setSelectedStatus: state.setSelectedStatus,
      selectedDeliveryDocket: state.selectedDeliveryDocket,
      setSelectedDeliveryDocket: state.setSelectedDeliveryDocket,
      selectedDrawingNumbers: state.selectedDrawingNumbers,
      setSelectedDrawingNumbers: state.setSelectedDrawingNumbers,
      selectedJobCodes: state.selectedJobCodes,
      setSelectedJobCodes: state.setSelectedJobCodes,
      selectedJobLocations: state.selectedJobLocations,
      setSelectedJobLocations: state.setSelectedJobLocations
    }))
  );

  // Store the original state when modal opens to restore on cancel
  const originalStateRef = useRef<{
    selectedStatus: string[];
    selectedDeliveryDocket: string[];
    selectedDrawingNumbers: string[];
    selectedJobCodes: string[];
    selectedJobLocations: string[];
  } | null>(null);

  // Effect to handle modal open/close logic
  useEffect(() => {
    if (isVisible) {
      // Store current state when modal opens
      originalStateRef.current = {
        selectedStatus: [...selectedStatus],
        selectedDeliveryDocket: [...selectedDeliveryDocket],
        selectedDrawingNumbers: [...selectedDrawingNumbers],
        selectedJobCodes: [...selectedJobCodes],
        selectedJobLocations: [...selectedJobLocations]
      };

      // Check if there are any applied filters (not all options selected)
      const allStatusIds = filters.statuses?.map(s => s.id) || [];
      const allDeliveryIds = filters.deliveryNumbers?.map(d => d.id) || [];
      const allDrawingIds = filters.drawingNumbers?.map(d => d.id) || [];
      const allJobCodeIds = filters.jobCodes?.map(j => j.id) || [];
      const allJobLocationIds = filters.jobLocations?.map(j => j.id) || [];

      const hasAppliedFilters =
        (selectedStatus.length > 0 && selectedStatus.length < allStatusIds.length) ||
        (selectedDeliveryDocket.length > 0 && selectedDeliveryDocket.length < allDeliveryIds.length) ||
        (selectedDrawingNumbers.length > 0 && selectedDrawingNumbers.length < allDrawingIds.length) ||
        (selectedJobCodes.length > 0 && selectedJobCodes.length < allJobCodeIds.length) ||
        (selectedJobLocations.length > 0 && selectedJobLocations.length < allJobLocationIds.length);

      if (!hasAppliedFilters) {
        // Reset to all options selected if no previous filters were applied
        setSelectedStatus(allStatusIds);
        setSelectedDeliveryDocket(allDeliveryIds);
        setSelectedDrawingNumbers(allDrawingIds);
        setSelectedJobCodes(allJobCodeIds);
        setSelectedJobLocations(allJobLocationIds);
      }
    }
  }, [isVisible, filters.statuses, filters.deliveryNumbers, filters.drawingNumbers, filters.jobCodes, filters.jobLocations]);

  const clearFilters = () => {
    onCancel();
    onClearFilters();
  };

  const applyFilters = () => {
    onCancel();
    onApplyFilters();
  };

  const handleCancel = () => {
    // Restore original state when canceling without applying
    if (originalStateRef.current) {
      setSelectedStatus(originalStateRef.current.selectedStatus);
      setSelectedDeliveryDocket(originalStateRef.current.selectedDeliveryDocket);
      setSelectedDrawingNumbers(originalStateRef.current.selectedDrawingNumbers);
      setSelectedJobCodes(originalStateRef.current.selectedJobCodes);
      setSelectedJobLocations(originalStateRef.current.selectedJobLocations);
    }
    onCancel();
  };

  return (
    <Modal
      open={isVisible}
      title={<span className="text-lg font-semibold text-gray-900">{TEXT_FILTERS}</span>}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={clearFilters} style={{ height: "40px" }}>
          {TEXT_CLEAR}
        </Button>,
        <Button key="apply" type="primary" onClick={applyFilters} style={{ backgroundColor: THEME.COLORS.PRIMARY, height: "40px" }}>
          {TEXT_APPLY}
        </Button>
      ]}
      styles={{
        body: {
          paddingTop: 24
        },
        footer: {
          paddingTop: 24
        }
      }}
      centered
      closeIcon={<CloseOutlined className="text-gray-700 cursor-pointer" />}
      width={400}
      wrapClassName="bottom-modal-wrapper"
      maskClosable={false}
    >
      <div>
        <div>
          <DropdownSelector title={JOB_CODE} options={filters.jobCodes} selected={selectedJobCodes} onSelect={setSelectedJobCodes} />
        </div>
        <div className="mt-3">
          <DropdownSelector
            title={DRAWING_NUMBER}
            options={filters.drawingNumbers}
            selected={selectedDrawingNumbers}
            onSelect={setSelectedDrawingNumbers}
          />
        </div>
        <div className="mt-3">
          <DropdownSelector title={JOB_LOCATION} options={filters.jobLocations} selected={selectedJobLocations} onSelect={setSelectedJobLocations} />
        </div>
        <div className="mt-3">
          <DropdownSelector title={STATUS} options={filters.statuses} selected={selectedStatus} onSelect={setSelectedStatus} canSearch={false} />
        </div>

        <div className="mt-3">
          <DropdownSelector
            title={DELIVERY_DOCKET}
            options={filters.deliveryNumbers}
            selected={selectedDeliveryDocket}
            onSelect={setSelectedDeliveryDocket}
          />
        </div>
      </div>
    </Modal>
  );
};
